/**
 * Frontend parsers for chat / browse-originated orders ("gift requests").
 *
 * Sprint 1.7+ orders coexist with legacy cake orders in the same `orders`
 * table. The marker is the `·` separator inside `cake_size`:
 *   - cake_type = SKU name           (e.g. "Anniversary Standard")
 *   - cake_size = "<vendor_category> · <vendor_name>"
 *                                    (e.g. "hamper · Yarra Valley Truffles")
 *
 * Mirrors supabase/functions/_shared/broadcast.ts so the customer UI
 * labels match the internal email + ops console. If you tweak one,
 * update the other.
 *
 * Why a parser instead of dedicated columns: keeping legacy + chat orders
 * in the same table avoids a schema migration mid-pivot and keeps
 * /admin/funnel + /orders + abandoned-checkout cron all reading from one
 * source. The cost is this little adapter, cheap and well-bounded.
 */
export interface ParsedGiftRequest {
  vendor: string | null;
  product: string;
  category: string; // lowercase, raw vendor_category (e.g. "hamper")
}

export interface GiftRequestOrderShape {
  cake_type: string | null;
  cake_size: string | null;
}

/**
 * True if this order was created by the chat / browse flow (Sprint 1.7+).
 * Legacy cake orders (created via /order) never have `·` in cake_size.
 */
export function isGiftRequestOrder(o: { cake_size: string | null }): boolean {
  return Boolean(o.cake_size && o.cake_size.includes("·"));
}

/**
 * Pull (vendor, product, category) out of a chat / browse order row.
 * Falls back gracefully when fields are missing, never throws.
 */
export function parseGiftRequest(o: GiftRequestOrderShape): ParsedGiftRequest {
  const csize = o.cake_size?.trim() || null;
  const ctype = o.cake_type?.trim() || null;

  if (csize && csize.includes("·")) {
    const [catRaw, ...vendorParts] = csize.split("·").map((s) => s.trim());
    return {
      vendor: vendorParts.join(" · ").trim() || null,
      product: ctype || "Gift",
      category: (catRaw || "").toLowerCase() || "gift",
    };
  }

  // Legacy cake order. Show the cake_type if present, else cake_size.
  return {
    vendor: null,
    product: ctype || csize || "Celebration package",
    category: "cake",
  };
}

/**
 * Capitalised, singular label used in customer-facing UI tags.
 * Falls back to "Gift" for unknown categories. Accepts "flowers"
 * as a legacy alias for "florist" (the orders CHECK constraint
 * tolerates both); also covers "voucher" which the orders enum
 * picked up post-MS216 but this helper hadn't been updated for.
 */
export function categoryLabel(category: string): string {
  switch (category.toLowerCase()) {
    case "cake":      return "Cake";
    case "hamper":    return "Hamper";
    case "florist":
    case "flowers":   return "Florist";
    case "workshop":  return "Workshop";
    case "spa":       return "Spa";
    case "experience":return "Experience";
    case "meal":      return "Meal";
    case "voucher":   return "Voucher";
    // MS278: Patricia Coffee, Proud Mary's, ST. ALi all live here.
    case "coffee":    return "Coffee";
    // MS-NF + MS615 + MS619
    case "drinkware": return "Drinkware";
    case "homewares": return "Homewares";
    case "plants":    return "Plants";
    case "books":     return "Books";
    default:          return "Gift";
  }
}

/**
 * Tiny pictograph next to the category label. Matches the Slack alert
 * vocabulary in broadcast.ts so customers + ops see the same icons.
 * Returns the bare emoji, caller decides on size / spacing.
 */
export function categoryEmoji(category: string): string {
  switch (category.toLowerCase()) {
    case "cake":      return "🎂";
    case "hamper":    return "🧺";
    case "florist":
    case "flowers":   return "🌸";
    case "workshop":  return "🎨";
    case "spa":       return "🛁";
    case "experience":return "✨";
    case "meal":      return "🍽️";
    case "voucher":   return "🎟️";
    // MS278: Patricia Coffee, Proud Mary's, ST. ALi all live here.
    case "coffee":    return "☕";
    // MS-NF: non-food sentimental gifts (drinkware + homewares).
    case "drinkware": return "🥤";
    case "homewares": return "🍵";
    // MS615: indoor plants (The Plant Society, Fitzroy Nursery).
    case "plants":    return "🪴";
    // MS619: books (Readings).
    case "books":     return "📚";
    default:          return "🎁";
  }
}

/**
 * Pretty-print a free-form chat occasion string. The chat LLM can submit
 * arbitrary text ("thank you", "team appreciation", "promotion") that
 * the legacy switch never covered. Snake_case → Title Case is the
 * minimum civilised treatment.
 */
export function occasionLabel(raw: string | null | undefined): string {
  if (!raw) return "Gift";
  // MS418: "welcome" → "First Day" to match the calendar, notifications,
  // order-confirmation emails, and every other customer-facing surface.
  // Was "Welcome", which was inconsistent with the rest of the platform.
  switch (raw) {
    case "birthday":         return "Birthday";
    case "work_anniversary": return "Work Anniversary";
    case "welcome":          return "First Day";
    case "farewell":         return "Farewell";
    case "milestone":        return "Milestone";
  }
  // Chat-originated free-form. snake_case + lowercase → Title Case.
  return raw
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}
